import * as ArrayOrObject from "./json";
import * as Lexer from "./lexer";
import * as NameOrIdentifier from "./nameOrIdentifier";
import * as PrimitiveLiteral from "./primitiveLiteral";
import * as Token from "./token";
import { equals, SourceArray, stringify } from "./utils";

function parseParameterAlias(
  value: SourceArray,
  index: number,
): Lexer.Token<Lexer.TokenType.ParameterAlias> | undefined {
  const at = Lexer.AT(value, index);
  if (!at) {
    return undefined;
  }
  const id = NameOrIdentifier.odataIdentifier(
    value,
    at,
    Lexer.TokenType.ODataIdentifier,
  );
  if (id) {
    return Lexer.tokenize(
      value,
      index,
      id.next,
      id.value,
      Lexer.TokenType.ParameterAlias,
    );
  }
}

export function commonExpr(
  value: SourceArray,
  index: number,
): Lexer.Token<Lexer.TokenType.CommonExpression> | undefined {
  let token: Token.CommonExpressionToken["value"] | undefined =
    PrimitiveLiteral.primitiveLiteral(value, index) ||
    parseParameterAlias(value, index) ||
    ArrayOrObject.arrayOrObject(value, index) ||
    rootExpr(value, index) ||
    methodCallExpr(value, index) ||
    firstMemberExpr(value, index) ||
    negateExpr(value, index) ||
    parenExpr(value, index) ||
    castExpr(value, index);

  if (!token) {
    return undefined;
  }

  const expr =
    addExpr(value, token.next) ||
    subExpr(value, token.next) ||
    mulExpr(value, token.next) ||
    divExpr(value, token.next) ||
    modExpr(value, token.next);

  if (expr) {
    token = new Lexer.Token({
      ...token,
      type: expr.type,
      value: {
        left: Lexer.clone(token),
        right: expr.value,
      },
      next: expr.value.next,
      raw: stringify(value, token.position, token.next),
    });
  }

  if (token) {
    return Lexer.tokenize(
      value,
      token.position,
      token.next,
      token,
      Lexer.TokenType.CommonExpression,
    );
  }
}

type BoolCommonExprToken = Token.TokenOfType<
  | Lexer.TokenType.IsOfExpression
  | Lexer.TokenType.MethodCallExpression
  | Lexer.TokenType.NotExpression
  | Lexer.TokenType.CommonExpression
  | Lexer.TokenType.BoolParenExpression
  | Lexer.TokenType.EqualsExpression
  | Lexer.TokenType.NotEqualsExpression
  | Lexer.TokenType.LesserThanExpression
  | Lexer.TokenType.LesserOrEqualsExpression
  | Lexer.TokenType.GreaterThanExpression
  | Lexer.TokenType.GreaterOrEqualsExpression
  | Lexer.TokenType.HasExpression
  | Lexer.TokenType.AndExpression
  | Lexer.TokenType.OrExpression
>;

export function boolCommonExpr(
  value: SourceArray,
  index: number,
): BoolCommonExprToken | undefined {
  let token: BoolCommonExprToken | undefined =
    isofExpr(value, index) ||
    boolMethodCallExpr(value, index) ||
    notExpr(value, index) ||
    commonExpr(value, index) ||
    boolParenExpr(value, index);

  if (!token) {
    return undefined;
  }

  if (token.type === Lexer.TokenType.CommonExpression) {
    const commonMoreExpr =
      eqExpr(value, token.next) ||
      neExpr(value, token.next) ||
      ltExpr(value, token.next) ||
      leExpr(value, token.next) ||
      gtExpr(value, token.next) ||
      geExpr(value, token.next) ||
      hasExpr(value, token.next);

    if (commonMoreExpr) {
      token = new Lexer.Token({
        ...token,
        type: commonMoreExpr.type,
        value: { left: token.value, right: commonMoreExpr.value },
        next: commonMoreExpr.value.next,
        raw: stringify(value, token.position, commonMoreExpr.value.next),
      });
    }
  }

  if (!token) {
    return undefined;
  }

  const expr = andExpr(value, token.next) || orExpr(value, token.next);

  if (expr) {
    const left = Lexer.clone(token);
    token = new Lexer.Token({
      ...token,
      type: expr.type,
      value: { left, right: expr.value },
      next: expr.value.next,
      raw: stringify(value, token.position, expr.value.next),
    });

    if (
      token?.type === Lexer.TokenType.AndExpression &&
      token.value.right.type === Lexer.TokenType.OrExpression
    ) {
      const left = Lexer.tokenize(
        value,
        token.value.left.position,
        token.value.right.value.left.next,
        {
          left: token.value.left,
          right: token.value.right.value.left,
        },
        token.type,
      );
      const right = token.value.right.value.right;
      token = new Lexer.Token({
        ...token,
        type: token.value.right.type,
        value: { left, right },
      });
    }
  }

  return token;
}

interface InternalAndOrExpression<
  T extends Lexer.TokenType.AndExpression | Lexer.TokenType.OrExpression,
> {
  type: T;
  value: Token.TokenTypeValue<T>["right"];
  position: number;
  next: number;
  raw: string;
}

interface InternalLeftRightExpression<T extends InternalLeftRightTokenType> {
  type: T;
  value: Token.TokenTypeValue<T>["right"];
  position: number;
  next: number;
  raw: string;
}

type InternalLeftRightTokenType =
  | Lexer.TokenType.EqualsExpression
  | Lexer.TokenType.NotEqualsExpression
  | Lexer.TokenType.LesserThanExpression
  | Lexer.TokenType.LesserOrEqualsExpression
  | Lexer.TokenType.GreaterThanExpression
  | Lexer.TokenType.GreaterOrEqualsExpression
  | Lexer.TokenType.HasExpression
  | Lexer.TokenType.AddExpression
  | Lexer.TokenType.SubExpression
  | Lexer.TokenType.MulExpression
  | Lexer.TokenType.DivExpression
  | Lexer.TokenType.ModExpression;

export function andExpr(
  value: SourceArray,
  index: number,
): InternalAndOrExpression<Lexer.TokenType.AndExpression> | undefined {
  let rws = Lexer.RWS(value, index);
  if (rws === index || !equals(value, rws, "and")) {
    return undefined;
  }
  const start = index;
  index = rws + 3;
  rws = Lexer.RWS(value, index);
  if (rws === index) {
    return undefined;
  }
  index = rws;
  const token = boolCommonExpr(value, index);
  if (!token) {
    return undefined;
  }

  return {
    type: Lexer.TokenType.AndExpression,
    value: token,
    position: start,
    next: index,
    raw: stringify(value, start, index),
  };
}

export function orExpr(
  value: SourceArray,
  index: number,
): InternalAndOrExpression<Lexer.TokenType.OrExpression> | undefined {
  let rws = Lexer.RWS(value, index);
  if (rws === index || !equals(value, rws, "or")) {
    return undefined;
  }
  const start = index;
  index = rws + 2;
  rws = Lexer.RWS(value, index);
  if (rws === index) {
    return undefined;
  }
  index = rws;
  const token = boolCommonExpr(value, index);
  if (!token) {
    return undefined;
  }

  return {
    type: Lexer.TokenType.OrExpression,
    value: token,
    position: start,
    next: index,
    raw: stringify(value, start, index),
  };
}

export function leftRightExpr<T extends InternalLeftRightTokenType>(
  value: SourceArray,
  index: number,
  expr: string,
  tokenType: T,
): InternalLeftRightExpression<T> | undefined {
  let rws = Lexer.RWS(value, index);
  if (rws === index) {
    return undefined;
  }
  const start = index;
  index = rws;
  if (!equals(value, index, expr)) {
    return undefined;
  }
  index += expr.length;
  rws = Lexer.RWS(value, index);
  if (rws === index) {
    return undefined;
  }
  index = rws;
  const token = commonExpr(value, index);
  if (!token) {
    return undefined;
  }

  return {
    type: tokenType,
    value: token.value,
    position: start,
    next: index,
    raw: stringify(value, start, index),
  };
}
export function eqExpr(
  value: SourceArray,
  index: number,
): InternalLeftRightExpression<Lexer.TokenType.EqualsExpression> | undefined {
  return leftRightExpr(value, index, "eq", Lexer.TokenType.EqualsExpression);
}
export function neExpr(
  value: SourceArray,
  index: number,
):
  | InternalLeftRightExpression<Lexer.TokenType.NotEqualsExpression>
  | undefined {
  return leftRightExpr(value, index, "ne", Lexer.TokenType.NotEqualsExpression);
}
export function ltExpr(
  value: SourceArray,
  index: number,
):
  | InternalLeftRightExpression<Lexer.TokenType.LesserThanExpression>
  | undefined {
  return leftRightExpr(
    value,
    index,
    "lt",
    Lexer.TokenType.LesserThanExpression,
  );
}
export function leExpr(
  value: SourceArray,
  index: number,
):
  | InternalLeftRightExpression<Lexer.TokenType.LesserOrEqualsExpression>
  | undefined {
  return leftRightExpr(
    value,
    index,
    "le",
    Lexer.TokenType.LesserOrEqualsExpression,
  );
}
export function gtExpr(
  value: SourceArray,
  index: number,
):
  | InternalLeftRightExpression<Lexer.TokenType.GreaterThanExpression>
  | undefined {
  return leftRightExpr(
    value,
    index,
    "gt",
    Lexer.TokenType.GreaterThanExpression,
  );
}
export function geExpr(
  value: SourceArray,
  index: number,
):
  | InternalLeftRightExpression<Lexer.TokenType.GreaterOrEqualsExpression>
  | undefined {
  return leftRightExpr(
    value,
    index,
    "ge",
    Lexer.TokenType.GreaterOrEqualsExpression,
  );
}
export function hasExpr(
  value: SourceArray,
  index: number,
): InternalLeftRightExpression<Lexer.TokenType.HasExpression> | undefined {
  return leftRightExpr(value, index, "has", Lexer.TokenType.HasExpression);
}

export function addExpr(
  value: SourceArray,
  index: number,
): InternalLeftRightExpression<Lexer.TokenType.AddExpression> | undefined {
  return leftRightExpr(value, index, "add", Lexer.TokenType.AddExpression);
}
export function subExpr(
  value: SourceArray,
  index: number,
): InternalLeftRightExpression<Lexer.TokenType.SubExpression> | undefined {
  return leftRightExpr(value, index, "sub", Lexer.TokenType.SubExpression);
}
export function mulExpr(
  value: SourceArray,
  index: number,
): InternalLeftRightExpression<Lexer.TokenType.MulExpression> | undefined {
  return leftRightExpr(value, index, "mul", Lexer.TokenType.MulExpression);
}
export function divExpr(
  value: SourceArray,
  index: number,
): InternalLeftRightExpression<Lexer.TokenType.DivExpression> | undefined {
  return leftRightExpr(value, index, "div", Lexer.TokenType.DivExpression);
}
export function modExpr(
  value: SourceArray,
  index: number,
): InternalLeftRightExpression<Lexer.TokenType.ModExpression> | undefined {
  return leftRightExpr(value, index, "mod", Lexer.TokenType.ModExpression);
}

export function notExpr(
  value: SourceArray,
  index: number,
): Lexer.Token<Lexer.TokenType.NotExpression> | undefined {
  if (!equals(value, index, "not")) {
    return undefined;
  }
  const start = index;
  index += 3;
  const rws = Lexer.RWS(value, index);
  if (rws === index) {
    return undefined;
  }
  index = rws;
  const token = boolCommonExpr(value, index);
  if (!token) {
    return undefined;
  }

  return Lexer.tokenize(
    value,
    start,
    token.next,
    token,
    Lexer.TokenType.NotExpression,
  );
}

export function boolParenExpr(
  value: SourceArray,
  index: number,
): Lexer.Token<Lexer.TokenType.BoolParenExpression> | undefined {
  const open = Lexer.OPEN(value, index);
  if (!open) {
    return undefined;
  }
  const start = index;
  index = open;
  index = Lexer.BWS(value, index);
  const token = boolCommonExpr(value, index);
  if (!token) {
    return undefined;
  }
  index = Lexer.BWS(value, token.next);
  const close = Lexer.CLOSE(value, index);
  if (!close) {
    return undefined;
  }
  index = close;

  return Lexer.tokenize(
    value,
    start,
    index,
    token,
    Lexer.TokenType.BoolParenExpression,
  );
}
export function parenExpr(
  value: SourceArray,
  index: number,
): Lexer.Token<Lexer.TokenType.ParenExpression> | undefined {
  const open = Lexer.OPEN(value, index);
  if (!open) {
    return undefined;
  }
  const start = index;
  index = open;
  index = Lexer.BWS(value, index);
  const token = commonExpr(value, index);
  if (!token) {
    return undefined;
  }
  index = Lexer.BWS(value, token.next);
  const close = Lexer.CLOSE(value, index);
  if (!close) {
    return undefined;
  }
  index = close;

  return Lexer.tokenize(
    value,
    start,
    index,
    token.value,
    Lexer.TokenType.ParenExpression,
  );
}

export function boolMethodCallExpr(
  value: SourceArray,
  index: number,
): Lexer.Token<Lexer.TokenType.MethodCallExpression> | undefined {
  return (
    endsWithMethodCallExpr(value, index) ||
    startsWithMethodCallExpr(value, index) ||
    containsMethodCallExpr(value, index) ||
    matchesPatternMethodCallExpr(value, index)
  );
}
export function methodCallExpr(
  value: SourceArray,
  index: number,
): Lexer.Token<Lexer.TokenType.MethodCallExpression> | undefined {
  return (
    indexOfMethodCallExpr(value, index) ||
    toLowerMethodCallExpr(value, index) ||
    toUpperMethodCallExpr(value, index) ||
    trimMethodCallExpr(value, index) ||
    substringMethodCallExpr(value, index) ||
    concatMethodCallExpr(value, index) ||
    lengthMethodCallExpr(value, index) ||
    yearMethodCallExpr(value, index) ||
    monthMethodCallExpr(value, index) ||
    dayMethodCallExpr(value, index) ||
    hourMethodCallExpr(value, index) ||
    minuteMethodCallExpr(value, index) ||
    secondMethodCallExpr(value, index) ||
    fractionalsecondsMethodCallExpr(value, index) ||
    dateMethodCallExpr(value, index) ||
    timeMethodCallExpr(value, index) ||
    roundMethodCallExpr(value, index) ||
    floorMethodCallExpr(value, index) ||
    ceilingMethodCallExpr(value, index) ||
    totalOffsetMinutesMethodCallExpr(value, index) ||
    minDateTimeMethodCallExpr(value, index) ||
    maxDateTimeMethodCallExpr(value, index) ||
    nowMethodCallExpr(value, index)
  );
}
export function methodCallExprFactory(
  value: SourceArray,
  index: number,
  method: string,
  min?: number,
  max?: number,
): Lexer.Token<Lexer.TokenType.MethodCallExpression> | undefined {
  if (min === undefined) {
    min = 0;
  }
  if (max === undefined) {
    max = min;
  }

  if (!equals(value, index, method)) {
    return undefined;
  }
  const start = index;
  index += method.length;
  const open = Lexer.OPEN(value, index);
  if (!open) {
    return undefined;
  }
  index = open;
  index = Lexer.BWS(value, index);
  let parameters: Token.MethodCallExpressionToken["value"]["parameters"] = [];
  if (min > 0) {
    parameters = [];
    while (parameters.length < max) {
      const expr = commonExpr(value, index);
      if (parameters.length < min && !expr) {
        return undefined;
      } else if (expr) {
        parameters.push(expr.value);
        index = expr.next;
        index = Lexer.BWS(value, index);
        const comma = Lexer.COMMA(value, index);
        if (parameters.length < min && !comma) {
          return undefined;
        }
        if (comma) {
          index = comma;
        } else {
          break;
        }
        index = Lexer.BWS(value, index);
      } else {
        break;
      }
    }
  }
  index = Lexer.BWS(value, index);
  const close = Lexer.CLOSE(value, index);
  if (!close) {
    return undefined;
  }
  index = close;

  return Lexer.tokenize(
    value,
    start,
    index,
    {
      method,
      parameters,
    },
    Lexer.TokenType.MethodCallExpression,
  );
}
export function containsMethodCallExpr(
  value: SourceArray,
  index: number,
): Lexer.Token<Lexer.TokenType.MethodCallExpression> | undefined {
  return methodCallExprFactory(value, index, "contains", 2);
}
export function startsWithMethodCallExpr(
  value: SourceArray,
  index: number,
): Lexer.Token<Lexer.TokenType.MethodCallExpression> | undefined {
  return methodCallExprFactory(value, index, "startswith", 2);
}
export function endsWithMethodCallExpr(
  value: SourceArray,
  index: number,
): Lexer.Token<Lexer.TokenType.MethodCallExpression> | undefined {
  return methodCallExprFactory(value, index, "endswith", 2);
}
export function lengthMethodCallExpr(
  value: SourceArray,
  index: number,
): Lexer.Token<Lexer.TokenType.MethodCallExpression> | undefined {
  return methodCallExprFactory(value, index, "length", 1);
}
export function indexOfMethodCallExpr(
  value: SourceArray,
  index: number,
): Lexer.Token<Lexer.TokenType.MethodCallExpression> | undefined {
  return methodCallExprFactory(value, index, "indexof", 2);
}
export function substringMethodCallExpr(
  value: SourceArray,
  index: number,
): Lexer.Token<Lexer.TokenType.MethodCallExpression> | undefined {
  return methodCallExprFactory(value, index, "substring", 2, 3);
}
export function toLowerMethodCallExpr(
  value: SourceArray,
  index: number,
): Lexer.Token<Lexer.TokenType.MethodCallExpression> | undefined {
  return methodCallExprFactory(value, index, "tolower", 1);
}
export function toUpperMethodCallExpr(
  value: SourceArray,
  index: number,
): Lexer.Token<Lexer.TokenType.MethodCallExpression> | undefined {
  return methodCallExprFactory(value, index, "toupper", 1);
}
export function trimMethodCallExpr(
  value: SourceArray,
  index: number,
): Lexer.Token<Lexer.TokenType.MethodCallExpression> | undefined {
  return methodCallExprFactory(value, index, "trim", 1);
}
export function concatMethodCallExpr(
  value: SourceArray,
  index: number,
): Lexer.Token<Lexer.TokenType.MethodCallExpression> | undefined {
  return methodCallExprFactory(value, index, "concat", 2);
}

export function yearMethodCallExpr(
  value: SourceArray,
  index: number,
): Lexer.Token<Lexer.TokenType.MethodCallExpression> | undefined {
  return methodCallExprFactory(value, index, "year", 1);
}
export function monthMethodCallExpr(
  value: SourceArray,
  index: number,
): Lexer.Token<Lexer.TokenType.MethodCallExpression> | undefined {
  return methodCallExprFactory(value, index, "month", 1);
}
export function dayMethodCallExpr(
  value: SourceArray,
  index: number,
): Lexer.Token<Lexer.TokenType.MethodCallExpression> | undefined {
  return methodCallExprFactory(value, index, "day", 1);
}
export function hourMethodCallExpr(
  value: SourceArray,
  index: number,
): Lexer.Token<Lexer.TokenType.MethodCallExpression> | undefined {
  return methodCallExprFactory(value, index, "hour", 1);
}
export function minuteMethodCallExpr(
  value: SourceArray,
  index: number,
): Lexer.Token<Lexer.TokenType.MethodCallExpression> | undefined {
  return methodCallExprFactory(value, index, "minute", 1);
}
export function secondMethodCallExpr(
  value: SourceArray,
  index: number,
): Lexer.Token<Lexer.TokenType.MethodCallExpression> | undefined {
  return methodCallExprFactory(value, index, "second", 1);
}
export function fractionalsecondsMethodCallExpr(
  value: SourceArray,
  index: number,
): Lexer.Token<Lexer.TokenType.MethodCallExpression> | undefined {
  return methodCallExprFactory(value, index, "fractionalseconds", 1);
}
export function dateMethodCallExpr(
  value: SourceArray,
  index: number,
): Lexer.Token<Lexer.TokenType.MethodCallExpression> | undefined {
  return methodCallExprFactory(value, index, "date", 1);
}
export function timeMethodCallExpr(
  value: SourceArray,
  index: number,
): Lexer.Token<Lexer.TokenType.MethodCallExpression> | undefined {
  return methodCallExprFactory(value, index, "time", 1);
}
export function totalOffsetMinutesMethodCallExpr(
  value: SourceArray,
  index: number,
): Lexer.Token<Lexer.TokenType.MethodCallExpression> | undefined {
  return methodCallExprFactory(value, index, "totaloffsetminutes", 1);
}

export function minDateTimeMethodCallExpr(
  value: SourceArray,
  index: number,
): Lexer.Token<Lexer.TokenType.MethodCallExpression> | undefined {
  return methodCallExprFactory(value, index, "mindatetime", 0);
}
export function maxDateTimeMethodCallExpr(
  value: SourceArray,
  index: number,
): Lexer.Token<Lexer.TokenType.MethodCallExpression> | undefined {
  return methodCallExprFactory(value, index, "maxdatetime", 0);
}
export function nowMethodCallExpr(
  value: SourceArray,
  index: number,
): Lexer.Token<Lexer.TokenType.MethodCallExpression> | undefined {
  return methodCallExprFactory(value, index, "now", 0);
}

export function roundMethodCallExpr(
  value: SourceArray,
  index: number,
): Lexer.Token<Lexer.TokenType.MethodCallExpression> | undefined {
  return methodCallExprFactory(value, index, "round", 1);
}
export function floorMethodCallExpr(
  value: SourceArray,
  index: number,
): Lexer.Token<Lexer.TokenType.MethodCallExpression> | undefined {
  return methodCallExprFactory(value, index, "floor", 1);
}
export function ceilingMethodCallExpr(
  value: SourceArray,
  index: number,
): Lexer.Token<Lexer.TokenType.MethodCallExpression> | undefined {
  return methodCallExprFactory(value, index, "ceiling", 1);
}

export function isofExpr(
  value: SourceArray,
  index: number,
): Lexer.Token<Lexer.TokenType.IsOfExpression> | undefined {
  if (!equals(value, index, "isof")) {
    return undefined;
  }
  const start = index;
  index += 4;
  const open = Lexer.OPEN(value, index);
  if (!open) {
    return undefined;
  }
  index = open;
  index = Lexer.BWS(value, index);
  const expr = commonExpr(value, index);
  if (expr) {
    index = expr.next;
    index = Lexer.BWS(value, index);
    const comma = Lexer.COMMA(value, index);
    if (!comma) {
      return undefined;
    }
    index = comma;
    index = Lexer.BWS(value, index);
  }
  const typeName = NameOrIdentifier.qualifiedTypeName(value, index);
  if (!typeName) {
    return undefined;
  }
  index = typeName.next;
  index = Lexer.BWS(value, index);
  const close = Lexer.CLOSE(value, index);
  if (!close) {
    return undefined;
  }
  index = close;

  return Lexer.tokenize(
    value,
    start,
    index,
    {
      target: expr,
      typename: typeName,
    },
    Lexer.TokenType.IsOfExpression,
  );
}
export function castExpr(
  value: SourceArray,
  index: number,
): Lexer.Token<Lexer.TokenType.CastExpression> | undefined {
  if (!equals(value, index, "cast")) {
    return undefined;
  }
  const start = index;
  index += 4;
  const open = Lexer.OPEN(value, index);
  if (!open) {
    return undefined;
  }
  index = open;
  index = Lexer.BWS(value, index);
  const expr = commonExpr(value, index);
  if (expr) {
    index = expr.next;
    index = Lexer.BWS(value, index);
    const comma = Lexer.COMMA(value, index);
    if (!comma) {
      return undefined;
    }
    index = comma;
    index = Lexer.BWS(value, index);
  }
  const typeName = NameOrIdentifier.qualifiedTypeName(value, index);
  if (!typeName) {
    return undefined;
  }
  index = typeName.next;
  index = Lexer.BWS(value, index);
  const close = Lexer.CLOSE(value, index);
  if (!close) {
    return undefined;
  }
  index = close;

  return Lexer.tokenize(
    value,
    start,
    index,
    {
      target: expr,
      typename: typeName,
    },
    Lexer.TokenType.CastExpression,
  );
}

export function negateExpr(
  value: SourceArray,
  index: number,
): Lexer.Token<Lexer.TokenType.NegateExpression> | undefined {
  if (value[index] !== 0x2d) {
    return undefined;
  }
  const start = index;
  index++;
  index = Lexer.BWS(value, index);
  const expr = commonExpr(value, index);
  if (!expr) {
    return undefined;
  }

  return Lexer.tokenize(
    value,
    start,
    expr.next,
    expr,
    Lexer.TokenType.NegateExpression,
  );
}

export function firstMemberExpr(
  value: SourceArray,
  index: number,
): Lexer.Token<Lexer.TokenType.FirstMemberExpression> | undefined {
  const token = inscopeVariableExpr(value, index);
  let member: Lexer.Token<Lexer.TokenType.MemberExpression> | undefined;
  const start = index;

  if (token) {
    if (value[token.next] === 0x2f) {
      index = token.next + 1;
      member = memberExpr(value, index);
      if (!member) {
        return undefined;
      }

      return Lexer.tokenize(
        value,
        start,
        member.next,
        [token, member],
        Lexer.TokenType.FirstMemberExpression,
      );
    }
  } else {
    member = memberExpr(value, index);
  }

  const firstToken = token || member;
  if (!firstToken) {
    return undefined;
  }

  return Lexer.tokenize(
    value,
    start,
    firstToken.next,
    firstToken,
    Lexer.TokenType.FirstMemberExpression,
  );
}
export function memberExpr(
  value: SourceArray,
  index: number,
): Lexer.Token<Lexer.TokenType.MemberExpression> | undefined {
  const start = index;
  const token = NameOrIdentifier.qualifiedEntityTypeName(value, index);

  if (token) {
    if (value[token.next] !== 0x2f) {
      return undefined;
    }
    index = token.next + 1;
  }

  const next = propertyPathExpr(value, index);

  if (!next) {
    return undefined;
  }
  return Lexer.tokenize(
    value,
    start,
    next.next,
    token ? { name: token, value: next } : next,
    Lexer.TokenType.MemberExpression,
  );
}
export function propertyPathExpr(
  value: SourceArray,
  index: number,
): Lexer.Token<Lexer.TokenType.PropertyPathExpression> | undefined {
  let token: Token.PropertyPathExpressionToken["value"] | undefined =
    NameOrIdentifier.odataIdentifier(
      value,
      index,
      Lexer.TokenType.ODataIdentifier,
    );
  const start = index;
  if (token) {
    index = token.next;
    const nav =
      collectionPathExpr(value, token.next) ||
      collectionNavigationExpr(value, token.next) ||
      singleNavigationExpr(value, token.next) ||
      complexPathExpr(value, token.next) ||
      singlePathExpr(value, token.next);

    if (nav) {
      index = nav.next;
      token = {
        current: Lexer.clone(token),
        next: nav,
      };
    }
  } else if (!token) {
    token = NameOrIdentifier.streamProperty(value, index);
    if (token) {
      index = token.next;
    }
  }

  if (!token) {
    return undefined;
  }
  return Lexer.tokenize(
    value,
    start,
    index,
    token,
    Lexer.TokenType.PropertyPathExpression,
  );
}
export function inscopeVariableExpr(
  value: SourceArray,
  index: number,
):
  | Lexer.Token<Lexer.TokenType.ImplicitVariableExpression>
  | Lexer.Token<Lexer.TokenType.LambdaVariableExpression>
  | Lexer.Token<Lexer.TokenType.ODataIdentifier>
  | undefined {
  return (
    implicitVariableExpr(value, index) ||
    (isLambdaPredicate ? lambdaVariableExpr(value, index) : undefined)
  );
}
export function implicitVariableExpr(
  value: SourceArray,
  index: number,
): Lexer.Token<Lexer.TokenType.ImplicitVariableExpression> | undefined {
  if (equals(value, index, "$it")) {
    return Lexer.tokenize(
      value,
      index,
      index + 3,
      "$it",
      Lexer.TokenType.ImplicitVariableExpression,
    );
  }
}
let isLambdaPredicate = false;
let hasLambdaVariableExpr = false;
export function lambdaVariableExpr(
  value: SourceArray,
  index: number,
): Lexer.Token<Lexer.TokenType.LambdaVariableExpression> | undefined {
  const token = NameOrIdentifier.odataIdentifier(
    value,
    index,
    Lexer.TokenType.LambdaVariableExpression,
  );
  if (token) {
    hasLambdaVariableExpr = true;
    return token;
  }
}
export function lambdaPredicateExpr(
  value: SourceArray,
  index: number,
): Lexer.Token<Lexer.TokenType.LambdaPredicateExpression> | undefined {
  isLambdaPredicate = true;
  const token = boolCommonExpr(value, index);
  isLambdaPredicate = false;
  if (token && hasLambdaVariableExpr) {
    hasLambdaVariableExpr = false;
    return Lexer.tokenize(
      value,
      token.position,
      token.next,
      token,
      Lexer.TokenType.LambdaPredicateExpression,
    );
  }
}
export function anyExpr(
  value: SourceArray,
  index: number,
): Lexer.Token<Lexer.TokenType.AnyExpression> | undefined {
  if (!equals(value, index, "any")) {
    return undefined;
  }
  const start = index;
  index += 3;
  const open = Lexer.OPEN(value, index);
  if (!open) {
    return undefined;
  }
  index = open;
  index = Lexer.BWS(value, index);
  const variable = lambdaVariableExpr(value, index);
  let predicate;
  if (variable) {
    index = variable.next;
    index = Lexer.BWS(value, index);
    const colon = Lexer.COLON(value, index);
    if (!colon) {
      return undefined;
    }
    index = colon;
    index = Lexer.BWS(value, index);
    predicate = lambdaPredicateExpr(value, index);
    if (!predicate) {
      return undefined;
    }
    index = predicate.next;
  }
  index = Lexer.BWS(value, index);
  const close = Lexer.CLOSE(value, index);
  if (!close) {
    return undefined;
  }
  index = close;

  return Lexer.tokenize(
    value,
    start,
    index,
    {
      variable,
      predicate,
    },
    Lexer.TokenType.AnyExpression,
  );
}
export function allExpr(
  value: SourceArray,
  index: number,
): Lexer.Token<Lexer.TokenType.AllExpression> | undefined {
  if (!equals(value, index, "all")) {
    return undefined;
  }
  const start = index;
  index += 3;

  const open = Lexer.OPEN(value, index);
  if (!open) {
    return undefined;
  }
  index = open;

  index = Lexer.BWS(value, index);
  const variable = lambdaVariableExpr(value, index);
  if (!variable) {
    return undefined;
  }
  index = variable.next;

  index = Lexer.BWS(value, index);

  const colon = Lexer.COLON(value, index);
  if (!colon) {
    return undefined;
  }
  index = colon;

  index = Lexer.BWS(value, index);

  const predicate = lambdaPredicateExpr(value, index);
  if (!predicate) {
    return undefined;
  }
  index = predicate.next;

  index = Lexer.BWS(value, index);

  const close = Lexer.CLOSE(value, index);
  if (!close) {
    return undefined;
  }
  index = close;

  return Lexer.tokenize(
    value,
    start,
    index,
    {
      variable,
      predicate,
    },
    Lexer.TokenType.AllExpression,
  );
}

export function collectionNavigationExpr(
  value: SourceArray,
  index: number,
): Lexer.Token<Lexer.TokenType.CollectionNavigationExpression> | undefined {
  const start = index;
  let entity, navigation, path;
  if (value[index] === 0x2f) {
    index++;
    entity = NameOrIdentifier.qualifiedEntityTypeName(value, index);
    if (!entity) {
      return undefined;
    }
    index = entity.next;
  }

  const predicate = keyPredicate(value, index);

  if (predicate) {
    index = predicate.next;
    navigation = singleNavigationExpr(value, index);
    if (navigation) {
      index = navigation.next;
    }
  } else {
    path = collectionPathExpr(value, index);
    if (path) {
      index = path.next;
    }
  }

  if (index > start) {
    return Lexer.tokenize(
      value,
      start,
      index,
      {
        entity,
        predicate,
        navigation,
        path,
      },
      Lexer.TokenType.CollectionNavigationExpression,
    );
  }
}

type KeyPredicateToken = Token.TokenOfType<
  Lexer.TokenType.SimpleKey | Lexer.TokenType.CompoundKey
>;

export function keyPredicate(
  value: SourceArray,
  index: number,
  metadataContext?: Lexer.MetadataContext,
): KeyPredicateToken | undefined {
  return simpleKey(value, index, metadataContext) || compoundKey(value, index);
}
export function simpleKey(
  value: SourceArray,
  index: number,
  metadataContext?: Lexer.MetadataContext,
): Lexer.Token<Lexer.TokenType.SimpleKey> | undefined {
  const open = Lexer.OPEN(value, index);
  if (!open) {
    return undefined;
  }
  const start = index;
  index = open;

  const token = keyPropertyValue(value, index);
  if (!token) {
    return undefined;
  }

  const close = Lexer.CLOSE(value, token.next);
  if (!close) {
    return undefined;
  }

  const key = metadataContext?.key?.propertyRefs?.[0]?.name;

  return Lexer.tokenize(
    value,
    start,
    close,
    { key, value: token },
    Lexer.TokenType.SimpleKey,
  );
}
export function compoundKey(
  value: SourceArray,
  index: number,
): Lexer.Token<Lexer.TokenType.CompoundKey> | undefined {
  const open = Lexer.OPEN(value, index);
  if (!open) {
    return undefined;
  }
  const start = index;
  index = open;

  let pair = keyValuePair(value, index);
  if (!pair) {
    return undefined;
  }

  const keys = [];
  while (pair) {
    keys.push(pair);
    const comma = Lexer.COMMA(value, pair.next);
    pair = comma ? keyValuePair(value, comma) : undefined;
  }

  index = keys.at(-1)?.next as number;
  const close = Lexer.CLOSE(value, index);
  if (!close) {
    return undefined;
  }
  index = close;

  return Lexer.tokenize(value, start, index, keys, Lexer.TokenType.CompoundKey);
}
export function keyValuePair(
  value: SourceArray,
  index: number,
): Lexer.Token<Lexer.TokenType.KeyValuePair> | undefined {
  const prop =
    NameOrIdentifier.primitiveKeyProperty(value, index) ||
    keyPropertyAlias(value, index);

  if (!prop) {
    return undefined;
  }
  const eq = Lexer.EQ(value, prop.next);
  if (!eq) {
    return undefined;
  }

  const val = keyPropertyValue(value, eq);
  if (val) {
    return Lexer.tokenize(
      value,
      index,
      val.next,
      {
        key: prop,
        value: val,
      },
      Lexer.TokenType.KeyValuePair,
    );
  }
}
export function keyPropertyValue(
  value: SourceArray,
  index: number,
): Lexer.Token<Lexer.TokenType.KeyPropertyValue> | undefined {
  const token = PrimitiveLiteral.primitiveLiteral(value, index);
  if (token) {
    return new Lexer.Token({
      ...token,
      type: Lexer.TokenType.KeyPropertyValue,
      value: String(token.value),
    });
  }
}
export function keyPropertyAlias(
  value: SourceArray,
  index: number,
): Lexer.Token<Lexer.TokenType.KeyPropertyAlias> | undefined {
  return NameOrIdentifier.odataIdentifier(
    value,
    index,
    Lexer.TokenType.KeyPropertyAlias,
  );
}

export function singleNavigationExpr(
  value: SourceArray,
  index: number,
): Lexer.Token<Lexer.TokenType.SingleNavigationExpression> | undefined {
  if (value[index] !== 0x2f) {
    return undefined;
  }
  const member = memberExpr(value, index + 1);
  if (member) {
    return Lexer.tokenize(
      value,
      index,
      member.next,
      member,
      Lexer.TokenType.SingleNavigationExpression,
    );
  }
}
export function collectionPathExpr(
  value: SourceArray,
  index: number,
): Lexer.Token<Lexer.TokenType.CollectionPathExpression> | undefined {
  let token: Token.CollectionPathExpressionToken["value"] | undefined;
  if (value[index] === 0x2f) {
    token = anyExpr(value, index + 1) || allExpr(value, index + 1);
  }

  if (token) {
    return Lexer.tokenize(
      value,
      index,
      token.next,
      token,
      Lexer.TokenType.CollectionPathExpression,
    );
  }
}

// Type definition adjustment for complexPathExpr value token array
/* // Removed type alias as it's not directly used in the fix
type ComplexPathExprValueToken = Token.TokenOfType<
  | Lexer.TokenType.QualifiedComplexTypeName
  | Lexer.TokenType.PropertyPathExpression
  | Lexer.TokenType.SinglePathExpression
>;
*/

export function complexPathExpr(
  value: SourceArray,
  index: number,
): Lexer.Token<Lexer.TokenType.ComplexPathExpression> | undefined {
  if (value[index] !== 0x2f) {
    return undefined;
  }
  const start = index;
  index++;
  const typeToken = NameOrIdentifier.qualifiedComplexTypeName(value, index);
  if (typeToken) {
    if (value[typeToken.next] !== 0x2f) {
      return undefined;
    }
    index = typeToken.next + 1;
  }

  // Removed boundFunctionExpr from the check
  const expr = propertyPathExpr(value, index) || singlePathExpr(value, index);

  if (expr) {
    // Pass a structured object instead of a tuple to avoid type issues
    const complexPathValue = {
      ...(typeToken && { typeName: typeToken }), // Include typeName only if it exists
      path: expr,
    };

    // Cast the value to the expected token value type to satisfy the compiler
    // This assumes the consuming code can handle this object structure.
    // Using 'as unknown as' because the types are fundamentally different now.
    return Lexer.tokenize(
      value,
      start,
      expr.next,
      complexPathValue as unknown as Token.ComplexPathExpressionToken["value"], // Use double cast
      Lexer.TokenType.ComplexPathExpression,
    );
  }
}
export function singlePathExpr(
  value: SourceArray,
  index: number,
): Lexer.Token<Lexer.TokenType.SinglePathExpression> | undefined {
  if (value[index] !== 0x2f) {
    return undefined;
  }
  return undefined;
}

// Re-add matchesPatternMethodCallExpr as it's used by platform tests
export function matchesPatternMethodCallExpr(
  value: SourceArray,
  index: number,
): Lexer.Token<Lexer.TokenType.MethodCallExpression> | undefined {
  return methodCallExprFactory(value, index, "matchespattern", 2);
}

export function rootExpr(
  value: SourceArray,
  index: number,
): Lexer.Token<Lexer.TokenType.RootExpression> | undefined {
  if (!equals(value, index, "$root/")) {
    return undefined;
  }
  const start = index;
  index += 6;

  const entitySet = NameOrIdentifier.entitySetName(value, index);
  let predicate:
      | Token.TokenOfType<Lexer.TokenType.SimpleKey>
      | Token.TokenOfType<Lexer.TokenType.CompoundKey>
      | undefined,
    entity: Token.TokenOfType<Lexer.TokenType.SingletonEntity> | undefined,
    tokenValue: Token.RootExpressionToken["value"]["current"];
  if (entitySet) {
    predicate = keyPredicate(value, entitySet.next);
  }
  if (entitySet && predicate) {
    tokenValue = {
      entitySet,
      keys: predicate,
    };
  } else {
    entity = NameOrIdentifier.singletonEntity(value, index);
    if (!entity) {
      return undefined;
    }
    tokenValue = {
      entity,
    };
  }

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  index = (predicate || entity)!.next;
  const nav = singleNavigationExpr(value, index);
  if (nav) {
    index = nav.next;
  }

  return Lexer.tokenize(
    value,
    start,
    index,
    {
      current: tokenValue,
      next: nav,
    },
    Lexer.TokenType.RootExpression,
  );
}
